/**
 * Панель фильтров списка «Чатов» — воронка у поиска: отчёт о выдаче сделки (не вернули, не забрали,
 * выдан частично), город, стадия сделки (где сделка чата
 * сейчас), рассылка (какую отправляли в чат и что нажал клиент), источник (официальный вход канала —
 * Wazzup, бот, Авито, группа ВК, Instagram — или канал в браузерах сотрудников), куда писали (канал → аккаунт или номер),
 * теги. Два режима одной панели (слово владельца 03.10.26): пока в поле сверху есть
 * текст — плоский список подходящих вариантов из всех разделов галочками («925 605», «инста
 * мск», «врн»); поле пустое — комбобоксы с мультивыбором, своим поиском и чипами выбранного.
 * Выбор живёт в адресе (`?report=&city=&stage=&sent=&source=&account=&tag=` повтором) и приходит из `Screen` вместе с
 * сеттерами и данными запросов; здесь — только состояние интерфейса.
 * Спека `docs/specs/2026-10-03-chat-filters-design.md`.
 */
import type { UseQueryResult } from "@tanstack/react-query";
import { ChevronDown, ListFilter, Search, X } from "lucide-react";
import { Fragment, type KeyboardEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import {
  type Channel,
  type ChatAccount,
  type ChatCity,
  type ChatFilterOptions,
  type ChatMailing,
  type ChatReport,
  type ChatStage,
  type ChatTagInfo,
  fmtAccountPhone,
} from "@/lib/clientChats";

export type FilterKey = "tag" | "city" | "account" | "source" | "stage" | "sent" | "report";
/** Канал — те же вкладки, что над списком чатов (`CHANNELS` страницы): подпись и короткое имя. */
export type ChannelInfo = { id: Channel; label: string; short: string };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channels: ChannelInfo[];
  /** Выбранное из адреса: id городов, ключи аккаунтов, источников и рассылок, коды стадий, имена тегов. */
  cities: string[];
  accounts: string[];
  sources: string[];
  stages: string[];
  mailings: string[];
  reports: string[];
  tags: string[];
  /** Сколько всего выбрано — бейдж на воронке. */
  picked: number;
  setPicked: (next: Partial<Record<FilterKey, string[]>>) => void;
  toggle: (key: FilterKey, now: string[], value: string) => void;
  filters: UseQueryResult<ChatFilterOptions>;
  allTags: UseQueryResult<{ tags: ChatTagInfo[] }>;
};

/** Как сотрудники зовут каналы словами — чтобы «ватсап» и «инстаграм» тоже находились. */
const ALIASES: Record<Channel, string> = {
  whatsapp: "ватсап вотсап вацап wa",
  telegram: "телеграм телега tg",
  max: "макс",
  avito: "авито",
  vk: "вк вконтакте",
  instagram: "инстаграм инста ig",
  livechat: "сайт онлайн-чат виджет",
};
/** Общий аккаунт канала без имени и номера — что это: боты Telegram и MAX, группа ВК. */
const PLAIN: Partial<Record<Channel, string>> = { telegram: "бот", max: "бот", vk: "группа", avito: "аккаунт" };
/**
 * Источник (разметка владельца 06.10.26): вкладка канала смешивает официальный вход с аккаунтами
 * сотрудников из браузера, источник их разводит — «Wazzup» и «WhatsApp в браузере», «TG-бот» и
 * «Telegram в браузере». Здесь — официальный вход: подпись и слова для поиска.
 */
const SOURCE: Record<Channel, { label: string; what: string }> = {
  whatsapp: { label: "Wazzup", what: "вазап номер whatsapp" },
  telegram: { label: "TG-бот", what: "tgbot бот" },
  max: { label: "MAX-бот", what: "maxbot бот" },
  avito: { label: "Авито", what: "avito" },
  vk: { label: "ВК", what: "vk группа" },
  instagram: { label: "Instagram", what: "аккаунты городов" },
  livechat: { label: "Сайт", what: "онлайн-чат виджет bitrix" },
};
const KIND: Record<FilterKey, string> = {
  city: "город",
  account: "аккаунт",
  tag: "тег",
  source: "источник",
  stage: "стадия",
  sent: "рассылка",
  report: "отчёт",
};
const WEB_HINT =
  "Переписка личного или рабочего аккаунта сотрудника: здесь её только видно, ответить можно в его браузере";
const NOTE = "px-2 py-1.5 text-[var(--tg-sec)]";

/** Вариант любого раздела в одном виде — так режим ключевых слов ищет по всем разделам сразу. */
type Option = {
  key: FilterKey;
  value: string;
  /** Полная подпись: «Москва», «Instagram · МСК», «MAX · «Сева-2» +7 925 933-67-44». */
  label: string;
  /** Подпись внутри своего канала, без его имени: «МСК», «бот», «+7 925 605-23-69». */
  name: string;
  /** Чип в комбобоксе — коротко: «МСК», «Инста МСК», «WA +7 925 605-23-69», «MAX «Сева-2»». */
  chip: string;
  /** Пояснение справа: «в браузере» — аккаунт сотрудника, ответить можно только там. */
  note: string;
  count: number;
  /** Слова для поиска строчными; цифры номера — отдельно, чтобы «925 605» нашло «+7 925 605-23-69». */
  words: string;
  digits: string;
  channel: Channel | null;
  web: boolean;
};
type Group = { caption: string; options: Option[] };

const cityOption = (c: ChatCity): Option => ({
  key: "city",
  value: String(c.id),
  label: c.name,
  name: c.name,
  chip: c.short || c.name,
  note: "",
  count: c.chats,
  words: `${c.name} ${c.short}`.toLowerCase(),
  digits: "",
  channel: null,
  web: false,
});
const tagOption = (t: ChatTagInfo): Option => ({
  key: "tag",
  value: t.name,
  label: t.name,
  name: t.name,
  chip: t.name,
  note: "",
  count: t.chats,
  words: t.name.toLowerCase(),
  digits: t.name.replace(/\D/g, ""),
  channel: null,
  web: false,
});
/** Отчёт о выдаче: в подписи — с разделом («Отчёт · Не забрали»), чтобы в поиске было видно, что это. */
const reportOption = (r: ChatReport): Option => ({
  key: "report",
  value: r.key,
  label: `Отчёт · ${r.name}`,
  name: r.name,
  chip: r.name,
  note: "",
  count: r.chats,
  words: `${r.name} ${r.group ?? ""} отчёт выдача сделка бронь кабинет лк`.toLowerCase(),
  digits: "",
  channel: null,
  web: false,
});
/** Стадия сделки: в подписи — с воронкой («Сдача · Новая»), в чипе и строке списка — одно имя. */
const stageOption = (s: ChatStage): Option => ({
  key: "stage",
  value: s.code,
  label: s.group ? `${s.group} · ${s.name}` : s.name,
  name: s.name,
  chip: s.name,
  note: "",
  count: s.chats,
  words: `${s.name} ${s.group} стадия сделка`.toLowerCase(),
  digits: "",
  channel: null,
  web: false,
});
/** Рассылка — названием; её кнопка — вместе с рассылкой («Как прошла примерка · Хорошо»): «Не нажали» есть у двух. */
const mailingOption = (m: ChatMailing): Option => {
  const step = m.key.includes(":");
  return {
    key: "sent",
    value: m.key,
    label: step ? `${m.title} · ${m.name}` : m.name,
    name: m.name,
    chip: step ? `${m.title}: ${m.name}` : m.name,
    note: "",
    count: m.chats,
    words: `${m.title} ${m.name} рассылка шаблон ${step ? "нажал кнопка" : ""}`.toLowerCase(),
    digits: "",
    channel: null,
    web: false,
  };
};
/** Источник известен и без ответа сервера (чип выбранного из адреса); число чатов — когда придёт. */
function sourceOption(ch: ChannelInfo, web: boolean, count: number): Option {
  const name = web ? `${ch.label} в браузере` : SOURCE[ch.id].label;
  return {
    key: "source",
    value: web ? `web:${ch.id}` : ch.id,
    label: `Источник: ${name}`,
    name,
    chip: web ? `${ch.short} в браузере` : name,
    note: "",
    count,
    words: [name, web ? "браузер сотрудники" : SOURCE[ch.id].what, ch.label, ch.short, ch.id, ALIASES[ch.id], "источник"]
      .join(" ")
      .toLowerCase(),
    digits: "",
    channel: null,
    web,
  };
}
/** Аккаунт и номер — одна сущность: у аккаунта просто может быть имя из CRM и/или номер. */
function accountOption(a: ChatAccount, ch: ChannelInfo): Option {
  const phone = a.phone ? fmtAccountPhone(a.phone) : "";
  // Имя номера из справочника CRM — в кавычках, как в шапке чата; город аккаунта Instagram — кодом как есть.
  const quoted = (a.web || a.phone) && a.name ? `«${a.name}»` : a.name;
  const name = [quoted, phone].filter(Boolean).join(" ") || PLAIN[a.channel] || ch.label;
  return {
    key: "account",
    value: a.key,
    label: `${ch.label} · ${name}`,
    name,
    // Чип короткий: у общего аккаунта Авито/ВК — один канал; «бот» — чтобы отличить от аккаунта сотрудника.
    chip: [ch.short, quoted || phone || (PLAIN[a.channel] === "бот" ? "бот" : "")].filter(Boolean).join(" "),
    note: a.web ? "в браузере" : "",
    count: a.chats,
    words: [ch.label, ch.short, ch.id, ALIASES[ch.id], a.name, phone, PLAIN[a.channel] ?? "", a.web ? "браузер" : "кабинет"]
      .join(" ")
      .toLowerCase(),
    digits: a.phone.replace(/\D/g, ""),
    channel: a.channel,
    web: a.web,
  };
}
/** Выбранное, которого нет среди вариантов (ещё грузится или уже пропало): чип, чтобы снять. */
const blank = (key: FilterKey, value: string, chip: string): Option => ({
  key,
  value,
  label: value,
  name: value,
  chip,
  note: "",
  count: 0,
  words: "",
  digits: "",
  channel: null,
  web: false,
});

const tokens = (q: string) => q.toLowerCase().split(/\s+/).filter(Boolean);
/** Все слова запроса должны найтись; слово из одних цифр («+7», «(925)», «605-23») ищем в цифрах номера. */
function hit(o: Option, words: string[]): boolean {
  return words.every((w) => {
    const digits = w.replace(/\D/g, "");
    return digits && digits === w.replace(/[\s()+-]/g, "") ? o.digits.includes(digits) : o.words.includes(w);
  });
}
const clamp = (n: number, len: number) => Math.min(Math.max(0, len - 1), Math.max(0, n));
/** Курсор клавиатуры обязан оставаться на виду: «вниз» в списке из 38 городов уводит за край. */
const scrollTo = (list: HTMLElement | null, index: number) =>
  list?.querySelector<HTMLElement>(`[data-i="${index}"]`)?.scrollIntoView({ block: "nearest" });

/** Воронка у поиска с числом выбранного; панель — пока открыта, её состояние живёт только тогда. */
export function ChatFilters(props: Props) {
  const { open, onOpenChange, picked } = props;
  return (
    <div className="relative flex-none" data-annot="фильтры">
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        title={picked ? `Фильтры: выбрано ${picked}` : "Фильтры: отчёт, город, стадия сделки, рассылка, источник, куда писали, теги"}
        aria-expanded={open}
        className={`relative grid size-10 place-items-center rounded-full ${
          picked ? "bg-[var(--tg-primary)] text-white" : "text-[var(--tg-sec)] hover:bg-[var(--tg-hover)]"
        }`}
      >
        <ListFilter size={20} />
        {picked > 0 && (
          <span className="absolute -right-0.5 -top-0.5 rounded-full bg-white px-1 text-[11px] font-medium leading-[16px] text-[var(--tg-primary)] ring-1 ring-[var(--tg-primary)]">
            {picked}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => onOpenChange(false)} />
          <Panel {...props} />
        </>
      )}
    </div>
  );
}

function Panel(props: Props) {
  const { onOpenChange, channels, cities, accounts, sources, stages, mailings, reports, tags, picked, setPicked, toggle, filters, allTags } = props;
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  // Открыт один раздел-комбобокс за раз; у «Куда писали» — ещё и шаг канала.
  const [section, setSection] = useState<FilterKey | "">("");
  const [channel, setChannel] = useState<Channel | "">("");
  const listRef = useRef<HTMLDivElement>(null);

  const reportOptions = useMemo(() => (filters.data?.reports ?? []).map(reportOption), [filters.data]);
  // Отчёты — группами, как их отдал сервер: «Выдача», «Личный кабинет».
  const reportGroups = useMemo(() => {
    const groups: Group[] = [];
    for (const o of reportOptions) {
      const caption = filters.data?.reports?.find((r) => r.key === o.value)?.group ?? "";
      const last = groups[groups.length - 1];
      if (last && last.caption === caption) last.options.push(o);
      else groups.push({ caption, options: [o] });
    }
    return groups;
  }, [reportOptions, filters.data]);
  const cityOptions = useMemo(() => (filters.data?.cities ?? []).map(cityOption), [filters.data]);
  const stageOptions = useMemo(() => (filters.data?.stages ?? []).map(stageOption), [filters.data]);
  // Стадии — группами по воронкам, как их отдал сервер: «Прокат», «Прокат — отказ», «Сдача»…
  const stageGroups = useMemo(() => {
    const groups: Group[] = [];
    for (const o of stageOptions) {
      const caption = filters.data?.stages?.find((s) => s.code === o.value)?.group ?? "";
      const last = groups[groups.length - 1];
      if (last && last.caption === caption) last.options.push(o);
      else groups.push({ caption, options: [o] });
    }
    return groups;
  }, [stageOptions, filters.data]);
  // Рассылки — сами списком, ниже по группе на рассылку с кнопками: что нажал клиент.
  const mailingOptions = useMemo(() => (filters.data?.mailings ?? []).map(mailingOption), [filters.data]);
  const mailingGroups = useMemo(() => {
    const groups: Group[] = [];
    for (const o of mailingOptions) {
      const caption = filters.data?.mailings?.find((m) => m.key === o.value)?.group ?? "";
      const last = groups[groups.length - 1];
      if (last && last.caption === caption) last.options.push(o);
      else groups.push({ caption, options: [o] });
    }
    return groups;
  }, [mailingOptions, filters.data]);
  // Официальный вход — у каждого канала; «в браузере» — где такие чаты есть или уже выбран в адресе.
  const sourceOptions = useMemo(() => {
    const known = filters.data?.sources ?? [];
    return channels.flatMap((ch) => {
      const count = (web: boolean) => known.find((s) => s.channel === ch.id && s.web === web)?.chats ?? 0;
      const browser = count(true) > 0 || sources.includes(`web:${ch.id}`);
      return [sourceOption(ch, false, count(false)), ...(browser ? [sourceOption(ch, true, count(true))] : [])];
    });
  }, [filters.data, channels, sources]);
  const accountOptions = useMemo(() => {
    const order = (o: Option) => channels.findIndex((c) => c.id === o.channel);
    return (filters.data?.accounts ?? [])
      .flatMap((a) => {
        const ch = channels.find((c) => c.id === a.channel);
        return ch ? [accountOption(a, ch)] : [];
      })
      .sort((a, b) => order(a) - order(b) || Number(a.web) - Number(b.web)); // каналы — в порядке вкладок, кабинет раньше браузера
  }, [filters.data, channels]);
  const tagOptions = useMemo(() => {
    const known = allTags.data?.tags ?? [];
    // Выбранный тег, которого уже нет ни на одном чате, оставляем в списке — чтобы его можно было снять.
    const gone = tags.filter((t) => allTags.data && !known.some((x) => x.name === t)).map((t) => ({ name: t, chats: 0 }));
    return [...known, ...gone].map(tagOption);
  }, [allTags.data, tags]);
  const all = useMemo(
    () => [...reportOptions, ...cityOptions, ...stageOptions, ...mailingOptions, ...sourceOptions, ...accountOptions, ...tagOptions],
    [reportOptions, cityOptions, stageOptions, mailingOptions, sourceOptions, accountOptions, tagOptions],
  );
  const now: Record<FilterKey, string[]> = { report: reports, city: cities, stage: stages, sent: mailings, source: sources, account: accounts, tag: tags };
  const loading = filters.isPending || allTags.isPending;
  const find = (key: FilterKey, value: string): Option =>
    all.find((o) => o.key === key && o.value === value) ?? blank(key, value, loading ? "…" : value);
  const flip = (o: Option) => toggle(o.key, now[o.key], o.value);

  // Режим ключевых слов: совпадения из всех разделов; первыми — у кого подпись начинается с
  // первого слова, чтобы Enter брал очевидное («мск» — Москва, а не «Инста МСК»).
  const found = useMemo(() => {
    const words = tokens(query);
    if (words.length === 0) return [];
    const first = words[0] ?? "";
    const rank = (o: Option) => (o.name.toLowerCase().startsWith(first) || o.chip.toLowerCase().startsWith(first) ? 0 : 1);
    return all
      .map((o, i) => ({ o, i, r: rank(o) }))
      .filter((x) => hit(x.o, words))
      .sort((a, b) => a.r - b.r || a.i - b.i)
      .map((x) => x.o);
  }, [all, query]);
  const searching = tokens(query).length > 0;
  const cur = clamp(cursor, found.length);
  // Тело в скобках: `scrollIntoView` в Chromium возвращает Promise, а React примет его за cleanup.
  useEffect(() => {
    scrollTo(listRef.current, cur);
  }, [cur]);
  const queryKeys = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape" && query) {
      e.stopPropagation(); // первый Esc чистит поле, второй — закрывает панель
      setQuery("");
      return;
    }
    if (!searching) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setCursor(clamp(cur + (e.key === "ArrowDown" ? 1 : -1), found.length));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const o = found[cur];
      if (o) flip(o);
    }
  };
  const chosen = [
    ...reports.map((v) => find("report", v)),
    ...cities.map((v) => find("city", v)),
    ...stages.map((v) => find("stage", v)),
    ...mailings.map((v) => find("sent", v)),
    ...sources.map((v) => find("source", v)),
    ...accounts.map((v) => find("account", v)),
    ...tags.map((v) => find("tag", v)),
  ];
  // Бейдж раздела в найденном: «город», «тег», а у аккаунта — его канал («WA», «Инста»), тогда в
  // подписи остаётся сам номер или имя и «+7 925 605-23-69» влезает целиком в 320 px.
  const kindOf = (o: Option) => (o.channel ? (channels.find((c) => c.id === o.channel)?.short ?? KIND.account) : KIND[o.key]);

  // «Куда писали» в два шага: канал, затем его аккаунты или номера. Канал с одним аккаунтом —
  // выбор канала уже выбор аккаунта, второй клик не нужен.
  const withAccounts = channels.filter((ch) => accountOptions.some((o) => o.channel === ch.id));
  const ofChannel = (id: Channel) => accountOptions.filter((o) => o.channel === id);
  const pickChannel = (id: Channel | "") => {
    setChannel(id);
    const only = id ? ofChannel(id) : [];
    if (only.length === 1 && only[0] && !accounts.includes(only[0].value)) flip(only[0]);
  };
  const pills: { id: Channel | ""; short: string; label: string }[] = [{ id: "", short: "Все", label: "Все каналы" }, ...withAccounts];
  const accountGroups: Group[] = channel
    ? [
        { caption: "Отвечаем из кабинета", options: ofChannel(channel).filter((o) => !o.web) },
        { caption: "Только в браузере сотрудника", options: ofChannel(channel).filter((o) => o.web) },
      ]
        .filter((g) => g.options.length > 0)
        .map((g) => ({ ...g, options: g.options.map((o) => ({ ...o, note: "" })) })) // подпись группы уже сказала
    : withAccounts.map((ch) => ({ caption: ch.label, options: ofChannel(ch.id) }));
  const channelHead = (
    <div className="border-b border-[var(--tg-sec)]/20 px-2 py-1.5" data-annot="канал">
      <div className="flex flex-wrap gap-1" role="group" aria-label="Канал">
        {pills.map((ch) => {
          const n = ch.id ? ofChannel(ch.id).filter((o) => accounts.includes(o.value)).length : 0;
          const on = channel === ch.id;
          return (
            <button
              key={ch.id}
              type="button"
              onClick={() => pickChannel(ch.id)}
              title={ch.label}
              aria-pressed={on}
              className={`flex items-center gap-1 rounded-full px-2.5 text-[12px] font-medium leading-[24px] ${
                on ? "bg-[var(--tg-primary)] text-white" : "bg-[var(--tg-surface)] text-[var(--tg-sec)] hover:text-[var(--tg-text)]"
              }`}
            >
              {ch.short}
              {n > 0 && (
                <span
                  className={`rounded-full px-1 text-[11px] leading-[16px] ${on ? "bg-white text-[var(--tg-primary)]" : "bg-[var(--tg-primary)] text-white"}`}
                >
                  {n}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {channel && ofChannel(channel).length > 1 && (
        <AllRow
          label={`Весь ${channels.find((c) => c.id === channel)?.label ?? ""}`}
          values={ofChannel(channel).map((o) => o.value)}
          selected={accounts}
          onChange={(next) => setPicked({ account: next })}
        />
      )}
    </div>
  );

  const filtersState = filters.isPending ? (
    <p className={NOTE}>Загрузка…</p>
  ) : filters.isError ? (
    <p className={NOTE}>Не удалось загрузить города и аккаунты</p>
  ) : null;
  const tagsState = allTags.isPending ? (
    <p className={NOTE}>Загрузка…</p>
  ) : allTags.isError ? (
    <p className={NOTE}>Не удалось загрузить теги</p>
  ) : tagOptions.length === 0 ? (
    <p className={NOTE}>Тегов пока нет. Откройте чат и добавьте тег в панели справа.</p>
  ) : null;
  const pick = (key: FilterKey) => ({
    selected: now[key],
    find: (v: string) => find(key, v),
    onToggle: (v: string) => toggle(key, now[key], v),
    onReset: () => setPicked({ [key]: [] }),
    open: section === key,
    onOpen: (open: boolean) => setSection(open ? key : ""),
  });

  // Правый край на 4 px правее воронки: при колонке в 320 px панель иначе уходила за левый край окна.
  return (
    <div
      className="absolute -right-1 top-11 z-20 w-[320px] max-w-[calc(100vw-24px)] rounded-[12px] bg-[var(--tg-surface)] p-1.5 text-[14px] shadow-[0_2px_12px_rgba(0,0,0,.28)]"
      onKeyDown={(e) => {
        if (e.key === "Escape") onOpenChange(false);
      }}
    >
      <div className="flex items-baseline px-2 py-1">
        <span className="flex-1 font-medium">Фильтры</span>
        {picked > 0 && (
          <button
            type="button"
            onClick={() => setPicked({ tag: [], city: [], account: [], source: [], stage: [], sent: [], report: [] })}
            className="text-[12px] text-[var(--tg-primary)] hover:underline"
          >
            сбросить все
          </button>
        )}
      </div>
      {/* Ключевые слова — быстрее любого списка: «925 605», «инста мск», «врн». Фокус сразу. */}
      <label
        className="mx-0.5 flex h-9 items-center gap-2 rounded-[10px] bg-[var(--tg-input)] px-2.5 text-[var(--tg-sec)]"
        data-annot="ключевые слова"
      >
        <Search size={16} className="flex-none" />
        <input
          autoFocus
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setCursor(0);
          }}
          onKeyDown={queryKeys}
          placeholder="Отчёт, город, стадия, рассылка, источник, номер, тег…"
          aria-label="Поиск по фильтрам"
          className="min-w-0 flex-1 bg-transparent text-[var(--tg-text)] outline-none placeholder:text-[var(--tg-sec)]"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            title="Очистить"
            className="grid size-6 flex-none place-items-center rounded-full hover:bg-[var(--tg-hover)]"
          >
            <X size={14} aria-hidden />
            <span className="sr-only">Очистить поиск</span>
          </button>
        )}
      </label>
      <div className="max-h-[calc(100dvh-170px)] overflow-y-auto pt-1">
        {searching ? (
          <>
            {/* Комбобоксы с чипами скрыты — выбранное показываем здесь, чтобы снять не выходя из поиска. */}
            {chosen.length > 0 && (
              <div className="flex flex-wrap gap-1 px-1 pb-1.5" data-annot="выбранные фильтры">
                {chosen.map((o) => (
                  <Chip key={`${o.key}:${o.value}`} option={o} onRemove={() => flip(o)} />
                ))}
              </div>
            )}
            <div ref={listRef} data-annot="найденные фильтры">
              {found.length === 0 && <p className={NOTE}>{loading ? "Загрузка…" : "Ничего не найдено"}</p>}
              {found.map((o, i) => (
                <Row
                  key={`${o.key}:${o.value}`}
                  option={o}
                  text={o.key === "stage" || o.key === "sent" || o.key === "report" ? o.label : o.name /* «Новая» есть в трёх воронках, «Не нажали» — у двух рассылок */}
                  index={i}
                  checked={now[o.key].includes(o.value)}
                  current={i === cur}
                  onToggle={() => flip(o)}
                  onHover={() => setCursor(i)}
                  kind={kindOf(o)}
                />
              ))}
            </div>
          </>
        ) : (
          <>
            <MultiPick
              title="Отчёт"
              hint="Сделки чата, где с выдачей что-то не так. «Не вернули» — «Ожидаем возврат», прокат закончился вчера или раньше, а вещь проката ещё у клиента (проданное не в счёт). «Не забрали» — «Забронировано», прокат начался раньше сегодня, а выдали не всё. «Выдан частично» — «Забронировано», часть товаров выдали, часть нет. Выдача — по копии CRM, обновляется раз в час. «ЛК: повторная примерка» и «ЛК: доставка» — клиент за 30 дней сам оставил заявку на повторку или заказал, изменил, отменил доставку на странице брони в личном кабинете. «Думают, есть сердечки» — сделка в «Думают», а в сетке примерки есть товары (кроме «не понравилось»): клиенту есть что предложить. «Похожие готовы» — сделка в «Думают», похожие на то, что клиент мерил, собраны, а коллаж со ссылкой ещё не клали в поле ответа; «Похожие отправлены» — клали за 30 дней"
              annot="отчёт"
              empty="Без отчёта"
              placeholder="отчёт"
              groups={reportGroups}
              state={filtersState ?? (reportOptions.length === 0 ? <p className={NOTE}>Отчёты пока не посчитаны</p> : null)}
              {...pick("report")}
            />
            <MultiPick
              title="Город"
              hint="Город чата: салон сделки, нет сделки — салон лида, нет и его — город аккаунта Instagram или объявления Авито"
              annot="город"
              empty="Любой город"
              placeholder="город или код"
              groups={[{ caption: "", options: cityOptions }]}
              state={filtersState ?? (cityOptions.length === 0 ? <p className={NOTE}>Город пока не определён ни у одного чата</p> : null)}
              {...pick("city")}
            />
            <MultiPick
              title="Стадия сделки"
              hint="Где сделка чата сейчас — по нашей копии Bitrix: та же стадия, что третьей строкой в списке. Стадии — по воронкам, отказы отдельно; «Без сделки» — у чата только лид или ничего"
              annot="стадия сделки"
              empty="Любая стадия"
              placeholder="стадия"
              groups={stageGroups}
              state={filtersState ?? (stageOptions.length === 0 ? <p className={NOTE}>Стадии пока не посчитаны</p> : null)}
              {...pick("stage")}
            />
            <MultiPick
              title="Рассылка"
              hint="Какую рассылку мы отправляли в чат — любым каналом: шаблоном WhatsApp, ботом, во ВКонтакте или Авито. У рассылок с кнопками ниже — что нажал клиент и «Не нажали»"
              annot="рассылка"
              empty="Любая рассылка"
              placeholder="рассылка или кнопка"
              groups={mailingGroups}
              state={filtersState ?? (mailingOptions.length === 0 ? <p className={NOTE}>Рассылок в чаты пока не было</p> : null)}
              {...pick("sent")}
            />
            <MultiPick
              title="Источник"
              hint="Откуда пришёл чат: официальный вход канала (Wazzup, боты Telegram и MAX, Авито, группа ВК, аккаунты Instagram) или аккаунты сотрудников в браузере (WhatsApp, Telegram, MAX) — отдельной строкой"
              annot="источник"
              empty="Любой источник"
              placeholder="источник"
              groups={[{ caption: "", options: sourceOptions }]}
              state={filtersState}
              {...pick("source")}
            />
            <MultiPick
              title="Куда писали"
              hint="Наш номер или аккаунт, на который писал клиент: сначала канал, затем аккаунт или номер. На общие аккаунты отвечаем из кабинета; переписку аккаунтов сотрудников здесь только видно"
              annot="куда писали"
              empty="Любой аккаунт или номер"
              placeholder="аккаунт или номер"
              groups={accountGroups}
              head={channelHead}
              state={filtersState ?? (accountOptions.length === 0 ? <p className={NOTE}>Чатов пока нет</p> : null)}
              {...pick("account")}
            />
            <MultiPick
              title="Теги"
              hint="Общие метки сотрудников на чатах"
              annot="теги"
              empty="Любые теги"
              placeholder="тег"
              groups={[{ caption: "", options: tagOptions }]}
              state={tagsState}
              {...pick("tag")}
            />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Комбобокс с мультивыбором: в закрытом — чипы выбранного с крестиком (или «Любой…»), в открытом —
 * свой поиск, список галочками и, если дан, свой шаг над списком (`head`). Раскрывается на месте,
 * а не поверх: внутри прокручиваемой панели всплывающий список обрезался бы её краем.
 */
function MultiPick(props: {
  title: string;
  hint: string;
  annot: string;
  /** Подпись пустого комбобокса — «Любой город»: без выбора раздел список не сужает. */
  empty: string;
  placeholder: string;
  groups: Group[];
  selected: string[];
  find: (value: string) => Option;
  onToggle: (value: string) => void;
  onReset: () => void;
  open: boolean;
  onOpen: (open: boolean) => void;
  /** Вместо списка: загрузка, ошибка, «пусто». */
  state: ReactNode;
  head?: ReactNode;
}) {
  const { open, onOpen, onToggle } = props;
  const [draft, setDraft] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const words = tokens(draft);
  const groups = props.groups
    .map((g) => ({ ...g, options: words.length ? g.options.filter((o) => hit(o, words)) : g.options }))
    .filter((g) => g.options.length > 0);
  const flat = groups.flatMap((g) => g.options);
  const cur = clamp(cursor, flat.length);
  // Открылся — сразу пишем в поиск, одно движение; закрылся — поиск чистый к следующему разу.
  useEffect(() => {
    if (open) inputRef.current?.focus();
    else setDraft("");
  }, [open]);
  useEffect(() => {
    scrollTo(listRef.current, cur);
  }, [cur]);
  const keys = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.stopPropagation(); // закрыть список, а не всю панель
      onOpen(false);
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setCursor(clamp(cur + (e.key === "ArrowDown" ? 1 : -1), flat.length));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const o = flat[cur];
      if (o) onToggle(o.value);
    }
  };
  let i = -1;
  return (
    <section className="px-0.5 pb-1" data-annot={props.annot}>
      <div className="flex items-baseline px-1.5 pb-1 pt-2 text-[12px] text-[var(--tg-sec)]" title={props.hint}>
        <span className="flex-1 font-medium uppercase tracking-wide">{props.title}</span>
        {props.selected.length > 0 && (
          <button type="button" onClick={props.onReset} className="text-[var(--tg-primary)] hover:underline">
            сбросить
          </button>
        )}
      </div>
      {/* Не <button>: внутри крестики чипов, а кнопка в кнопке — невалидно. */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => onOpen(!open)}
        onKeyDown={(e) => {
          if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            onOpen(!open);
          }
        }}
        className={`flex min-h-9 cursor-pointer items-center gap-1 rounded-[10px] bg-[var(--tg-input)] py-1 pl-2 pr-1.5 ${
          open ? "ring-1 ring-[var(--tg-primary)]" : ""
        }`}
      >
        <div className="flex min-w-0 flex-1 flex-wrap gap-1">
          {props.selected.length === 0 ? (
            <span className="leading-[22px] text-[var(--tg-sec)]">{props.empty}</span>
          ) : (
            props.selected.map((v) => <Chip key={v} option={props.find(v)} onRemove={() => onToggle(v)} />)
          )}
        </div>
        <ChevronDown size={16} className={`flex-none text-[var(--tg-sec)] transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
      </div>
      {open && (
        <div className="mt-1 overflow-hidden rounded-[10px] bg-[var(--tg-input)]">
          <label className="flex h-9 items-center gap-2 border-b border-[var(--tg-sec)]/20 px-2.5 text-[var(--tg-sec)]">
            <Search size={14} className="flex-none" />
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setCursor(0);
              }}
              onKeyDown={keys}
              placeholder={props.placeholder}
              aria-label={`Поиск: ${props.title}`}
              className="min-w-0 flex-1 bg-transparent text-[var(--tg-text)] outline-none placeholder:text-[var(--tg-sec)]"
            />
          </label>
          {props.head}
          <div ref={listRef} className="max-h-[240px] overflow-y-auto py-1">
            {props.state ??
              (flat.length === 0 ? (
                <p className={NOTE}>Ничего не найдено</p>
              ) : (
                groups.map((g) => (
                  <Fragment key={g.caption}>
                    {g.caption && <p className="px-2 pb-0.5 pt-1.5 text-[12px] text-[var(--tg-sec)]">{g.caption}</p>}
                    {g.options.map((o) => {
                      i += 1;
                      const at = i;
                      return (
                        <Row
                          key={o.value}
                          option={o}
                          text={o.name}
                          index={at}
                          checked={props.selected.includes(o.value)}
                          current={at === cur}
                          onToggle={() => onToggle(o.value)}
                          onHover={() => setCursor(at)}
                        />
                      );
                    })}
                  </Fragment>
                ))
              ))}
          </div>
        </div>
      )}
    </section>
  );
}

/** Строка с галочкой: подпись, пояснение («в браузере»), раздел (в режиме ключевых слов), чатов. */
function Row(props: {
  option: Option;
  text: string;
  index: number;
  checked: boolean;
  current: boolean;
  onToggle: () => void;
  onHover: () => void;
  kind?: string;
}) {
  const o = props.option;
  return (
    <label
      data-i={props.index}
      onMouseEnter={props.onHover}
      className={`flex cursor-pointer items-center gap-2 px-2 py-1.5 ${props.current ? "bg-[var(--tg-hover)]" : ""}`}
    >
      <input type="checkbox" checked={props.checked} onChange={props.onToggle} className="size-4 flex-none accent-[var(--tg-primary)]" />
      <span className="min-w-0 flex-1 truncate" title={o.label}>
        {props.text}
      </span>
      {o.note && (
        <span className="flex-none text-[12px] text-[var(--tg-sec)]" title={WEB_HINT}>
          {o.note}
        </span>
      )}
      {props.kind && (
        <span className="flex-none rounded-[4px] bg-[var(--tg-hover)] px-1 text-[11px] leading-[16px] text-[var(--tg-sec)]">{props.kind}</span>
      )}
      <span className="min-w-6 flex-none text-right text-[12px] tabular-nums text-[var(--tg-sec)]">{o.count}</span>
    </label>
  );
}

/** Чип выбранного с крестиком; живёт и внутри кликабельного комбобокса — клик по крестику туда не уходит. */
function Chip({ option, onRemove }: { option: Option; onRemove: () => void }) {
  return (
    <span
      className="inline-flex max-w-full items-center gap-0.5 rounded-full bg-[var(--tg-primary)]/10 pl-2 pr-0.5 text-[12px] leading-[22px] text-[var(--tg-primary)]"
      title={option.label}
    >
      <span className="truncate">{option.chip}</span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        title={`Снять: ${option.label}`}
        className="grid size-[18px] flex-none place-items-center rounded-full hover:bg-[var(--tg-primary)]/20"
      >
        <X size={12} aria-hidden />
        <span className="sr-only">Снять {option.label}</span>
      </button>
    </span>
  );
}

/** «Весь канал»: выбрать все его аккаунты разом — на сервере ключа канала нет, выражаем ключами. */
function AllRow(props: { label: string; values: string[]; selected: string[]; onChange: (next: string[]) => void }) {
  const { values, selected } = props;
  const on = values.filter((v) => selected.includes(v)).length;
  const every = on === values.length;
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = on > 0 && !every; // часть аккаунтов канала — «минус» в галочке
  }, [on, every]);
  return (
    <label className="mt-1.5 flex cursor-pointer items-center gap-2 rounded-[8px] px-1 py-1 font-medium hover:bg-[var(--tg-hover)]">
      <input
        ref={ref}
        type="checkbox"
        checked={every}
        onChange={() =>
          props.onChange(every ? selected.filter((v) => !values.includes(v)) : [...selected, ...values.filter((v) => !selected.includes(v))])
        }
        className="size-4 flex-none accent-[var(--tg-primary)]"
      />
      <span className="flex-1">{props.label}</span>
      <span className="text-[12px] font-normal text-[var(--tg-sec)]">{values.length}</span>
    </label>
  );
}
